import type { CompanyGroupRecord, CompanyLocationRecord, CompanyProfile } from '../admin/clients/api';

export type AdminSettingsCompany = {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
  logo_mark_url: string | null;
};

export type AdminSettingsProfile = {
  id: number;
  company_id: number;
  display_name: string;
  legal_name: string | null;
  short_code: string | null;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  support_contact_email: string | null;
  support_contact_phone: string | null;
  time_zone: string | null;
  branding_json: Record<string, unknown> | null;
  default_schedule_horizon_days: number;
  status: 'onboarding' | 'active' | 'inactive';
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminSettingsSetupCycle = {
  id: number;
  company_id: number;
  sequence_number: number;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  archived_at: string | null;
  summary: Record<string, unknown> | null;
};

export type AdminSettingsSectionRecord = {
  id: number;
  company_setup_cycle_id: number;
  section_key: string;
  status: 'not_started' | 'draft' | 'needs_review' | 'blocked' | 'waiting_on_scheduler_admin' | 'complete';
  completion_percent: number;
  started_at: string | null;
  completed_at: string | null;
  last_saved_at: string | null;
  blocker_summary: string | null;
  validation_summary?: Record<string, unknown> | null;
  draft_summary?: Record<string, unknown> | null;
  blockers?: string[];
};

export type AdminSettingsReadinessSummary = {
  ready: boolean;
  blockers: string[];
};

export type AdminSettingsOverviewResponse = {
  selected_company: AdminSettingsCompany;
  available_companies: AdminSettingsCompany[];
  profile: AdminSettingsProfile;
  active_setup_cycle: AdminSettingsSetupCycle;
  sections: AdminSettingsSectionRecord[];
  group_portfolio: Array<{
    id: number;
    name: string;
    slug: string;
    kind: string;
    department: { id: number; name: string; slug: string } | null;
    status: 'Active' | 'Archived' | 'Draft' | 'Inactive' | 'Needs attention' | 'Not yet started' | 'Onboarding';
    providers: {
      total: number;
      active: number;
      onboarding: number;
      pending: number;
    };
  }>;
  department_hierarchy: Array<{
    id: number;
    name: string;
    slug: string;
    status: string;
    scheduler_admins: SchedulerAdminCoverageRecord[];
    schedule_groups: Array<{
      id: number;
      name: string;
      slug: string;
      status: string;
      direct_scheduler_admins: SchedulerAdminCoverageRecord[];
      effective_scheduler_admins: SchedulerAdminCoverageRecord[];
    }>;
  }>;
  unassigned_schedule_groups: Array<{
    id: number;
    name: string;
    slug: string;
    status: string;
    direct_scheduler_admins: SchedulerAdminCoverageRecord[];
  }>;
  onboarding_draft_routing: {
    candidate_count: number;
    batch_count: number;
    packages: Array<{
      package_key: string;
      package_type: 'spreadsheet' | 'drafts';
      name: string;
      source_kind: string;
      source_version: string | null;
      source_document: string | null;
      source_checksum: string | null;
      rows_count: number;
      candidates_count: number;
      candidate_kinds: Record<string, number>;
    }>;
    schedule_groups: Array<{
      id: number;
      name: string;
      slug: string;
      department: { id: number; name: string; slug: string } | null;
    }>;
  };
  company_activation_readiness: AdminSettingsReadinessSummary;
  first_schedule_draft_readiness: AdminSettingsReadinessSummary;
  counts: Record<string, number>;
  next_recommended_action: string;
};

export type SchedulerAdminCoverageRecord = {
  id: number;
  name: string;
  email: string | null;
  status: string;
};

export type AdminSettingsReadinessResponse = {
  selected_company: AdminSettingsCompany;
  overall_readiness: {
    ready: boolean;
  };
  company_activation_readiness: AdminSettingsReadinessSummary;
  first_schedule_draft_readiness: AdminSettingsReadinessSummary;
  counts: Record<string, number>;
};

export type AdminSettingsPaginated<T> = {
  current_page: number;
  data: T[];
  last_page: number;
  per_page: number;
  total: number;
};

export type AdminSettingsLocationRecord = {
  id: number;
  name: string;
  code: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state_region: string | null;
  postal_code: string | null;
  country_code: string | null;
  notes: string | null;
  color_hex: string | null;
  sort_order: number;
  status: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminSettingsScheduleTypeRecord = {
  id: number;
  name: string;
  code: string;
  description: string | null;
  color_hex: string | null;
  sort_order: number;
  is_system_seeded: boolean;
  status: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminSettingsServiceLineRecord = {
  id: number;
  schedule_type_id: number;
  name: string;
  code: string | null;
  description: string | null;
  color_hex: string | null;
  sort_order: number;
  coverage_mode: 'location_bound' | 'companywide';
  status: string;
  schedule_type?: Pick<AdminSettingsScheduleTypeRecord, 'id' | 'name' | 'code' | 'status'>;
  locations?: Array<Pick<AdminSettingsLocationRecord, 'id' | 'name' | 'code' | 'status'>>;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminSettingsTeamRecord = {
  id: number;
  service_line_id: number;
  name: string;
  code: string | null;
  description: string | null;
  color_hex: string | null;
  sort_order: number;
  status: string;
  service_line?: Pick<AdminSettingsServiceLineRecord, 'id' | 'name' | 'code' | 'status'>;
  locations?: Array<Pick<AdminSettingsLocationRecord, 'id' | 'name' | 'code' | 'status'>>;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminSettingsScopeUserRecord = {
  id: number;
  user_id: number | null;
  full_name: string;
  invite_email: string;
  invite_status: 'not_invited' | 'invited' | 'accepted' | 'active' | 'deactivated' | 'expired';
  company_membership_status: 'missing' | 'invited' | 'active' | 'inactive';
  invite_sent_at: string | null;
  activated_at: string | null;
  deactivated_at: string | null;
  invite_expires_at: string | null;
  is_primary: boolean;
  user?: {
    id: number;
    name: string;
    email: string;
    status: string;
  } | null;
};

export type AdminSettingsSchedulerAdminScopeRecord = {
  id: number;
  schedule_type_id: number;
  service_line_id: number;
  location_id: number | null;
  team_id: number | null;
  schedule_id: number | null;
  scope_level: 'service_line' | 'location' | 'team';
  status: string;
  handoff_status: 'not_sent' | 'invited' | 'pending_acceptance' | 'active' | 'complete';
  notes: string | null;
  schedule_type?: Pick<AdminSettingsScheduleTypeRecord, 'id' | 'name' | 'code' | 'status'>;
  service_line?: Pick<AdminSettingsServiceLineRecord, 'id' | 'name' | 'code' | 'status'>;
  location?: Pick<AdminSettingsLocationRecord, 'id' | 'name' | 'code' | 'status'> | null;
  team?: Pick<AdminSettingsTeamRecord, 'id' | 'name' | 'code' | 'status'> | null;
  scope_users?: AdminSettingsScopeUserRecord[];
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminSettingsScheduleRecord = {
  id: number;
  schedule_type_id: number;
  service_line_id: number;
  location_id: number | null;
  team_id: number | null;
  owning_scheduler_admin_scope_id: number | null;
  name: string;
  code: string | null;
  description: string | null;
  color_hex: string | null;
  sort_order: number;
  status: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminSettingsResource =
  | 'locations'
  | 'schedule-types'
  | 'service-lines'
  | 'teams'
  | 'scheduler-admin-scopes'
  | 'schedules';

export type AdminSettingsRecordMap = {
  locations: AdminSettingsLocationRecord;
  'schedule-types': AdminSettingsScheduleTypeRecord;
  'service-lines': AdminSettingsServiceLineRecord;
  teams: AdminSettingsTeamRecord;
  'scheduler-admin-scopes': AdminSettingsSchedulerAdminScopeRecord;
  schedules: AdminSettingsScheduleRecord;
};

export type AdminSettingsSectionResponse = {
  selected_company: AdminSettingsCompany;
  section: AdminSettingsSectionRecord;
  rows: unknown[];
  validation_summary: Record<string, unknown> | null;
  linked_import_batches: Array<Record<string, unknown>>;
  readiness_blockers: string[];
};

export type AdminSettingsValidationError = {
  message?: string;
  errors?: Record<string, string[]>;
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const buildUrl = (path: string, tenantQuery = '') => {
  const query = tenantQuery.trim();
  return query ? `${path}?${query}` : path;
};

const readJson = async <T>(response: Response): Promise<T> => {
  if (response.ok) {
    return response.json() as Promise<T>;
  }

  let message = `Admin settings request failed (${response.status}).`;

  try {
    const payload = (await response.json()) as AdminSettingsValidationError;
    if (payload.message) {
      message = payload.message;
    } else if (payload.errors) {
      const firstMessage = Object.values(payload.errors).flat()[0];
      if (firstMessage) {
        message = firstMessage;
      }
    }
  } catch {
    // Ignore JSON parsing failures and keep the generic message.
  }

  throw new Error(message);
};

const requestJson = async <T>(path: string, init: RequestInit = {}, tenantQuery = ''): Promise<T> => {
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (init.method && init.method !== 'GET' && init.method !== 'HEAD' && csrfToken) {
    headers.set('X-CSRF-TOKEN', csrfToken);
  }

  const response = await fetch(buildUrl(path, tenantQuery), {
    credentials: 'same-origin',
    ...init,
    headers,
  });

  return readJson<T>(response);
};

export const fetchAdminSettingsOverview = (tenantQuery: string) =>
  requestJson<AdminSettingsOverviewResponse>('/api/v1/secure/admin-settings/overview', {}, tenantQuery);

export const routeAdminSettingsOnboardingPackage = (
  tenantQuery: string,
  packageKey: string,
  groupId: number
) => requestJson<{
  routed: true;
  message: string;
  batch_id: number;
  candidate_count: number;
  group: { id: number; name: string; slug: string };
}>(
  '/api/v1/secure/admin-settings/onboarding-drafts/route',
  {
    method: 'POST',
    body: JSON.stringify({ group_id: groupId, package_key: packageKey }),
  },
  tenantQuery
);

export const fetchAdminSettingsReadiness = (tenantQuery: string) =>
  requestJson<AdminSettingsReadinessResponse>('/api/v1/secure/admin-settings/readiness', {}, tenantQuery);

export const fetchAdminSettingsCompanyProfile = (tenantQuery: string) =>
  requestJson<{ profile: CompanyProfile }>('/api/v1/secure/admin-settings/company-profile', {}, tenantQuery)
    .then((response) => response.profile);

export const updateAdminSettingsCompanyProfile = async (tenantQuery: string, payload: FormData) => {
  const response = await fetch(buildUrl('/api/v1/secure/admin-settings/company-profile', tenantQuery), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: payload,
  });

  if (response.status === 422) {
    const validation = (await response.json()) as AdminSettingsValidationError;
    const error = new Error(validation.message ?? 'Please check the company profile fields and try again.');
    (error as Error & { validation?: AdminSettingsValidationError }).validation = validation;
    throw error;
  }

  return (await readJson<{ profile: CompanyProfile }>(response)).profile;
};

export const fetchSharedCompanyLocations = (tenantQuery: string) =>
  requestJson<{ data: CompanyLocationRecord[] }>('/api/v1/secure/admin-settings/shared-locations', {}, tenantQuery)
    .then((response) => response.data);

export const saveSharedCompanyLocation = (
  tenantQuery: string,
  locationId: number | null,
  payload: Record<string, string | boolean>
) => requestJson<{ record: CompanyLocationRecord }>(
  locationId === null
    ? '/api/v1/secure/admin-settings/shared-locations'
    : `/api/v1/secure/admin-settings/shared-locations/${locationId}`,
  {
    method: locationId === null ? 'POST' : 'PATCH',
    body: JSON.stringify(payload),
  },
  tenantQuery
).then((response) => response.record);

export const deleteSharedCompanyLocation = (tenantQuery: string, locationId: number) =>
  requestJson<{ deleted: boolean }>(
    `/api/v1/secure/admin-settings/shared-locations/${locationId}`,
    { method: 'DELETE' },
    tenantQuery
  );

export const fetchSharedCompanyGroups = (tenantQuery: string) =>
  requestJson<{ data: CompanyGroupRecord[] }>('/api/v1/secure/admin-settings/shared-groups', {}, tenantQuery)
    .then((response) => response.data);

export const saveSharedCompanyGroup = (
  tenantQuery: string,
  groupId: number | null,
  payload: Record<string, string | number | null>
) => requestJson<{ record: CompanyGroupRecord }>(
  groupId === null
    ? '/api/v1/secure/admin-settings/shared-groups'
    : `/api/v1/secure/admin-settings/shared-groups/${groupId}`,
  {
    method: groupId === null ? 'POST' : 'PATCH',
    body: JSON.stringify(payload),
  },
  tenantQuery
).then((response) => response.record);

export const deleteSharedCompanyGroup = (tenantQuery: string, groupId: number) =>
  requestJson<{ deleted: boolean }>(
    `/api/v1/secure/admin-settings/shared-groups/${groupId}`,
    { method: 'DELETE' },
    tenantQuery
  );

export const updateAdminSettingsProfile = (tenantQuery: string, payload: Partial<AdminSettingsProfile>) =>
  requestJson<{ selected_company: AdminSettingsCompany; profile: AdminSettingsProfile }>(
    '/api/v1/secure/admin-settings/profile',
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    tenantQuery
  );

export const createAdminSettingsSetupCycle = (tenantQuery: string, payload: { reason: string; from_cycle_id?: number | null }) =>
  requestJson<{ selected_company: AdminSettingsCompany; cycle: AdminSettingsSetupCycle & { sections?: AdminSettingsSectionRecord[] } }>(
    '/api/v1/secure/admin-settings/setup-cycles',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    tenantQuery
  );

export const archiveAdminSettingsSetupCycle = (tenantQuery: string, cycleId: number) =>
  requestJson<{ archived: true; cycle: AdminSettingsSetupCycle }>(
    `/api/v1/secure/admin-settings/setup-cycles/${cycleId}/archive`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
    tenantQuery
  );

export const reopenAdminSettingsSection = (tenantQuery: string, cycleId: number, sectionKey: string) =>
  requestJson<{ section: AdminSettingsSectionRecord }>(
    `/api/v1/secure/admin-settings/setup-cycles/${cycleId}/sections/${sectionKey}/reopen`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
    tenantQuery
  );

export const fetchAdminSettingsSection = (tenantQuery: string, sectionKey: string) =>
  requestJson<AdminSettingsSectionResponse>(`/api/v1/secure/admin-settings/sections/${sectionKey}`, {}, tenantQuery);

export const updateAdminSettingsSection = (
  tenantQuery: string,
  sectionKey: string,
  payload: {
    status: AdminSettingsSectionRecord['status'];
    completion_percent?: number;
    blocker_summary?: string | null;
    draft_summary?: Record<string, unknown> | null;
    validation_summary?: Record<string, unknown> | null;
  }
) =>
  requestJson<{ section: AdminSettingsSectionRecord }>(
    `/api/v1/secure/admin-settings/sections/${sectionKey}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    tenantQuery
  );

export const fetchAdminSettingsResource = <TResource extends AdminSettingsResource>(
  tenantQuery: string,
  resource: TResource,
  params: Record<string, string> = {}
) => {
  const searchParams = new URLSearchParams(tenantQuery);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== '') {
      searchParams.set(key, value);
    }
  });

  return requestJson<{
    resource: TResource;
    selected_company: AdminSettingsCompany;
    data: AdminSettingsPaginated<AdminSettingsRecordMap[TResource]>;
  }>(`/api/v1/secure/admin-settings/${resource}`, {}, searchParams.toString());
};

export const createAdminSettingsResource = <TResource extends AdminSettingsResource>(
  tenantQuery: string,
  resource: TResource,
  payload: Record<string, unknown>
) =>
  requestJson<{ resource: TResource; record: AdminSettingsRecordMap[TResource] }>(
    `/api/v1/secure/admin-settings/${resource}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    tenantQuery
  );

export const updateAdminSettingsResource = <TResource extends AdminSettingsResource>(
  tenantQuery: string,
  resource: TResource,
  recordId: number,
  payload: Record<string, unknown>
) =>
  requestJson<{ resource: TResource; record: AdminSettingsRecordMap[TResource] }>(
    `/api/v1/secure/admin-settings/${resource}/${recordId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    tenantQuery
  );

export const deleteAdminSettingsResource = <TResource extends AdminSettingsResource>(
  tenantQuery: string,
  resource: TResource,
  recordId: number
) =>
  requestJson<{ resource: TResource; deleted: true }>(
    `/api/v1/secure/admin-settings/${resource}/${recordId}`,
    {
      method: 'DELETE',
    },
    tenantQuery
  );

export const reorderAdminSettingsResource = <TResource extends AdminSettingsResource>(
  tenantQuery: string,
  resource: TResource,
  ids: number[]
) =>
  requestJson<{ resource: TResource; reordered: true }>(
    `/api/v1/secure/admin-settings/${resource}/reorder`,
    {
      method: 'POST',
      body: JSON.stringify({ ids }),
    },
    tenantQuery
  );
